import { AdminRole } from "@repo/api-dtos"
import {
	Activity,
	BadgePercent,
	Ban,
	Barcode,
	Bot,
	Boxes,
	BrainCircuit,
	CalendarCheck,
	CalendarClock,
	ClipboardCheck,
	Code2,
	CreditCard,
	Database,
	FerrisWheel,
	FileSpreadsheet,
	Filter,
	Fingerprint,
	FlaskConical,
	GalleryHorizontal,
	Gamepad2,
	Gauge,
	Gavel,
	GitCompareArrows,
	Globe,
	Handshake,
	Inbox,
	KeyRound,
	Landmark,
	Layers,
	LayoutDashboard,
	LineChart,
	ListChecks,
	ListTree,
	Mail,
	Megaphone,
	MessageSquareWarning,
	Monitor,
	Package,
	Plug,
	Radar,
	Radio,
	Receipt,
	Rocket,
	Scale,
	ScanLine,
	ScrollText,
	Server,
	Settings2,
	Shield,
	ShieldAlert,
	ShieldCheck,
	ShoppingBag,
	Siren,
	Stamp,
	Star,
	Tag,
	Tags,
	Ticket,
	TicketPercent,
	ToggleRight,
	Trophy,
	Truck,
	Users,
	Wallet,
	Wrench,
} from "lucide-react"

/**
 * Mirrors `marketplaceReadProcedure` on the backend. Gating these entries on
 * `MarketplaceRead` alone hides them from the order-support and
 * user-management admins whose endpoints already accept them.
 */
const MARKETPLACE_NAV_ROLES = [
	AdminRole.MarketplaceRead,
	AdminRole.MarketplaceOrderSupport,
	AdminRole.MarketplaceUserManagement,
	AdminRole.MarketplaceCoupons,
]

/**
 * Coupons, creator codes, referral contests and buyer promotions: read by
 * marketplace readers, operated by the coupons role.
 */
const PROMOTIONS_NAV_ROLES = [
	AdminRole.MarketplaceRead,
	AdminRole.MarketplaceCoupons,
]

export const NAV_GROUPS = {
	overview: "overview",
	marketplace: "marketplace",
	promotions: "promotions",
	finance: "finance",
	catalogue: "catalogue",
	scanner: "scanner",
	api: "api",
	tools: "tools",
	system: "system",
} as const

export type NavGroup = (typeof NAV_GROUPS)[keyof typeof NAV_GROUPS]

/** Badges whose number comes from a polled query rather than a static label. */
export type LiveBadgeKind = "moderationOpen" | "couponRequestsPending"

export type NavIcon = React.ComponentType<{ className?: string }>

/** One screen (route) of a page. A page with several tabs shows a tab bar. */
export interface NavTab {
	title: string
	description?: string
	href: string
	/** Other paths that also belong to this tab (prefix match for the sidebar, exact match for the tab bar). */
	activePaths?: string[]
	icon: NavIcon
	liveBadge?: LiveBadgeKind
	/** Visible when the admin has any of these roles; visible to everyone when absent. */
	requiredRoles?: AdminRole[]
}

/**
 * An entry of a group's page panel; links to its first visible tab and shows
 * the live badge of its visible tabs.
 */
export interface NavPage {
	title: string
	icon: NavIcon
	badge?: string
	tabs: NavTab[]
}

/** One rail button: a group of pages. */
export interface NavGroupConfig {
	key: NavGroup
	label: string
	icon: NavIcon
	pages: NavPage[]
}

/** One tab flattened with its page and group, for the command palette and the environment switch. */
export interface NavItem extends NavTab {
	group: NavGroup
	groupLabel: string
	pageTitle: string
	badge?: string
}

const isLocalhost =
	typeof window !== "undefined" &&
	window.location.hostname === "localhost" &&
	window.location.port === "3001"

const isProduction =
	typeof window !== "undefined" &&
	window.location.hostname === "admin.cardnexus.com"

export const ENVIRONMENTS = [
	{
		name: "Production",
		value: "production",
		icon: Globe,
		color: "text-green-600",
		href: "https://admin.cardnexus.com",
	},
	{
		name: "Staging",
		value: "staging",
		icon: Server,
		color: "text-yellow-600",
		href: "https://admin-staging.cardnexus.com",
	},
	{
		name: "Development",
		value: "development",
		icon: Code2,
		color: "text-blue-600",
		href: "https://admin-dev.cardnexus.com",
	},
	...(isLocalhost
		? [
				{
					name: "Local",
					value: "local",
					icon: Monitor,
					color: "text-gray-600",
					href: "http://localhost:3001",
				},
			]
		: []),
] as const

/** A page made of a single screen: its only tab carries the route, roles and description. */
function singlePage(tab: NavTab, extra: Pick<NavPage, "badge"> = {}): NavPage {
	return { title: tab.title, icon: tab.icon, tabs: [tab], ...extra }
}

const marketplaceGroup: NavGroupConfig = {
	key: NAV_GROUPS.marketplace,
	label: "Marketplace",
	icon: ShoppingBag,
	pages: [
		{
			title: "Orders",
			icon: ShoppingBag,
			tabs: [
				{
					title: "Orders",
					href: "/orders",
					icon: ShoppingBag,
					description: "View and manage marketplace orders",
					requiredRoles: MARKETPLACE_NAV_ROLES,
				},
				{
					title: "Dispute settings",
					href: "/configuration/disputes",
					icon: Gavel,
					description:
						"Model, prompts, policy, tone of voice and reference terms used by the AI dispute analyst",
					requiredRoles: [
						AdminRole.Superadmin,
						AdminRole.MarketplaceOrderSupport,
					],
				},
			],
		},
		singlePage({
			title: "Offers",
			href: "/offers",
			icon: Handshake,
			description:
				"Buyer and seller price negotiations: offers, counters, deals",
			requiredRoles: MARKETPLACE_NAV_ROLES,
		}),
		singlePage({
			title: "Transactions",
			href: "/transactions",
			icon: CreditCard,
			description: "View and manage payment transactions",
			requiredRoles: MARKETPLACE_NAV_ROLES,
		}),
		singlePage({
			title: "Users",
			href: "/users",
			icon: Users,
			description: "View and manage platform users",
			requiredRoles: MARKETPLACE_NAV_ROLES,
		}),
		singlePage({
			title: "Review appeals",
			href: "/review-appeals",
			icon: MessageSquareWarning,
			description: "Sellers and buyers contesting the review they received",
			requiredRoles: MARKETPLACE_NAV_ROLES,
		}),
		{
			title: "Moderation",
			icon: ShieldAlert,
			tabs: [
				{
					title: "Queue",
					href: "/moderation",
					icon: ShieldAlert,
					description:
						"Flagged messages by account: confirm or dismiss automatic suspensions",
					requiredRoles: [AdminRole.MarketplaceUserManagement],
					liveBadge: "moderationOpen",
				},
				{
					title: "IP bans",
					href: "/moderation/ip-bans",
					icon: Ban,
					description: "Addresses blocked from every API request",
					requiredRoles: [AdminRole.MarketplaceUserManagement],
				},
				{
					title: "Settings",
					href: "/configuration/moderation",
					icon: Filter,
					description:
						"Model, thresholds and prompt used by the message classifier",
					requiredRoles: [
						AdminRole.Superadmin,
						AdminRole.MarketplaceUserManagement,
					],
				},
			],
		},
		{
			title: "Shipping",
			icon: Truck,
			tabs: [
				{
					title: "Rates",
					href: "/shipping-rates",
					icon: Truck,
					description:
						"Buyer shipping prices per country for SendCloud-activated markets",
					requiredRoles: [AdminRole.MarketplaceFinance],
				},
				{
					title: "Postage stamps",
					href: "/postage-stamps",
					icon: Stamp,
					description: "Prepaid La Poste stamp stock and sheet ingestion",
					requiredRoles: [
						AdminRole.MarketplaceRead,
						AdminRole.MarketplaceOrderSupport,
					],
				},
				{
					title: "Settings",
					href: "/configuration/shipping",
					icon: Mail,
					description:
						"Untracked letters per market: switch, gates, order value caps, closure and coverage",
					requiredRoles: [
						AdminRole.Superadmin,
						AdminRole.MarketplaceOrderSupport,
					],
				},
			],
		},
		singlePage({
			title: "Carousels",
			href: "/configuration/carousels",
			icon: GalleryHorizontal,
			description:
				"How the home page, Explore and seller shop carousels rank listings, with a preview",
			requiredRoles: [AdminRole.Superadmin, AdminRole.MarketplaceOrderSupport],
		}),
		singlePage({
			title: "Pricing",
			href: "/configuration/pricing",
			icon: Tags,
			description:
				"Price check and Reprice access, Good deals for every condition, the Price check email, with previews",
			requiredRoles: [AdminRole.Superadmin, AdminRole.MarketplaceOrderSupport],
		}),
	],
}

const promotionsGroup: NavGroupConfig = {
	key: NAV_GROUPS.promotions,
	label: "Promotions",
	icon: BadgePercent,
	pages: [
		singlePage({
			title: "Seller promotions",
			href: "/seller-promotions",
			icon: BadgePercent,
			description:
				"Reduced seller fees for sellers who connect an app, a region or sellers you pick",
			requiredRoles: [
				AdminRole.MarketplaceRead,
				AdminRole.MarketplaceCoupons,
				AdminRole.MarketplaceFinance,
			],
		}),
		singlePage({
			title: "Buyer promotions",
			href: "/buyer-promotions",
			icon: TicketPercent,
			description:
				"No buyer fee or an automatic discount at checkout, by country or shop",
			requiredRoles: PROMOTIONS_NAV_ROLES,
		}),
		singlePage({
			title: "Coupons",
			href: "/coupons",
			icon: Ticket,
			description: "Create and manage promotional coupons",
			requiredRoles: PROMOTIONS_NAV_ROLES,
		}),
		singlePage({
			title: "Coupon requests",
			href: "/coupon-requests",
			icon: Inbox,
			description:
				"Campaigns Pro sellers ask CardNexus to co-fund, waiting for a decision",
			requiredRoles: PROMOTIONS_NAV_ROLES,
			liveBadge: "couponRequestsPending",
		}),
		{
			title: "Referrals",
			icon: Handshake,
			tabs: [
				{
					title: "Referrals",
					href: "/referrals",
					icon: Handshake,
					description:
						"Every checkout paid with a referral code, its discount, its reward and the flags to review",
					requiredRoles: PROMOTIONS_NAV_ROLES,
				},
				{
					title: "Batches",
					href: "/referrals/batches",
					icon: CalendarCheck,
					description:
						"The weekly reward release: review the batch, then it pays",
					requiredRoles: PROMOTIONS_NAV_ROLES,
				},
				{
					title: "Creator codes",
					href: "/creator-codes",
					icon: Megaphone,
					description: "Referral codes issued to content creators",
					requiredRoles: PROMOTIONS_NAV_ROLES,
				},
				{
					title: "Settings",
					href: "/configuration/referral",
					icon: Settings2,
					description:
						"Give, Get, covered transactions, reward validity and the release slot",
					requiredRoles: [AdminRole.Superadmin, ...PROMOTIONS_NAV_ROLES],
				},
				{
					title: "Referral contests",
					href: "/contests",
					icon: Trophy,
					description:
						"The past referral contests: dates, standings, closing and awards",
					requiredRoles: PROMOTIONS_NAV_ROLES,
				},
			],
		},
		singlePage({
			title: "Events",
			href: "/live-events",
			icon: FerrisWheel,
			description: "Card-fair booth: prize wheel, quests, draws and broadcasts",
			requiredRoles: [AdminRole.EventsManage],
		}),
	],
}

const FINANCE_ROLES = [AdminRole.MarketplaceFinance]

const financeGroup: NavGroupConfig = {
	key: NAV_GROUPS.finance,
	label: "Finance",
	icon: Landmark,
	pages: [
		{
			title: "Accounting",
			icon: FileSpreadsheet,
			tabs: [
				{
					title: "Overview",
					href: "/accounting",
					icon: FileSpreadsheet,
					description: "Monthly closes and VAT remittances",
					requiredRoles: FINANCE_ROLES,
				},
				{
					title: "Monthly closes",
					href: "/accounting/monthly-closes",
					icon: CalendarCheck,
					description: "Per-region, per-month accounting closes",
					requiredRoles: FINANCE_ROLES,
				},
				{
					title: "VAT declarations",
					href: "/accounting/vat-declarations",
					icon: Receipt,
					description:
						"The declaration pack: OSS, IOSS, the French return, UK/Swiss VAT provisions",
					requiredRoles: FINANCE_ROLES,
				},
				{
					title: "VAT remittances",
					href: "/accounting/vat-remittances",
					icon: Landmark,
					description:
						"Filings made to tax authorities, linked to monthly closes",
					requiredRoles: FINANCE_ROLES,
				},
				{
					title: "Stripe reconciliation",
					href: "/accounting/reconciliation",
					icon: Scale,
					description:
						"Persistent Stripe ledger: monthly statements, exceptions and ledger search",
					requiredRoles: FINANCE_ROLES,
				},
			],
		},
	],
}

const CATALOGUE_ROLES = [AdminRole.CatalogueRead]

const catalogueGroup: NavGroupConfig = {
	key: NAV_GROUPS.catalogue,
	label: "Catalogue",
	icon: Package,
	pages: [
		singlePage({
			title: "Games",
			href: "/games",
			icon: Gamepad2,
			description: "Manage your game library and details",
			requiredRoles: CATALOGUE_ROLES,
		}),
		{
			title: "Expansions",
			icon: Boxes,
			tabs: [
				{
					title: "Featured",
					href: "/catalogue/featured-expansions",
					icon: Star,
					description: "Pin and order the expansions each game page highlights",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Groups",
					href: "/expansion-groups",
					icon: Boxes,
					description: "Organize your expansions into groups",
					requiredRoles: CATALOGUE_ROLES,
				},
			],
		},
		singlePage({
			title: "Products",
			href: "/products",
			icon: Barcode,
			description: "Browse and edit all products: singles and sealed",
			requiredRoles: CATALOGUE_ROLES,
		}),
		singlePage({
			title: "Calendar",
			href: "/catalogue/upcoming",
			icon: CalendarClock,
			description:
				"Expected expansions: release dates, completion against TCGplayer and alerts",
			requiredRoles: CATALOGUE_ROLES,
		}),
		{
			title: "Catalogue review",
			icon: GitCompareArrows,
			tabs: [
				{
					title: "Review",
					href: "/catalogue-v2",
					icon: GitCompareArrows,
					description:
						"Pending catalogue changes grouped into decisions: renames, merges, removals, field changes",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Manage diff",
					href: "/diff",
					icon: Rocket,
					description: "View, manage and deploy pending changes by game",
					requiredRoles: CATALOGUE_ROLES,
				},
			],
		},
		{
			title: "Imports",
			icon: ListTree,
			tabs: [
				{
					title: "Tasks",
					href: "/tasks",
					icon: ListTree,
					description: "View, manage and execute tasks",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Price imports",
					href: "/tools/price-imports",
					icon: LineChart,
					description:
						"Nightly price-import runs by date, per game and marketplace",
					requiredRoles: CATALOGUE_ROLES,
				},
			],
		},
		{
			title: "Price crawl",
			icon: Radar,
			tabs: [
				{
					title: "Overview",
					href: "/crawl",
					icon: Gauge,
					description:
						"Whether the crawl is on time, what is left to crawl and what comes next",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Speed and health",
					href: "/crawl/health",
					icon: Activity,
					description:
						"Requests over time, why the speed changes and recent failures",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Prices",
					href: "/crawl/prices",
					icon: Tags,
					description: "How up to date the stored prices are, per game",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Spending",
					href: "/crawl/spending",
					icon: Wallet,
					description:
						"Zyte spending against the contract and the cost per product",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Data",
					href: "/crawl/data",
					icon: Database,
					description:
						"Which products have prices, by language and condition, and what is stored for one card",
					requiredRoles: CATALOGUE_ROLES,
				},
				{
					title: "Settings",
					href: "/crawl/settings",
					icon: Settings2,
					description:
						"Games, speed, budget and how often products are crawled",
					requiredRoles: [AdminRole.Superadmin, AdminRole.CatalogueRead],
				},
			],
		},
	],
}

const SCANNER_ROLES = [AdminRole.ScannerAdmin]

const scannerGroup: NavGroupConfig = {
	key: NAV_GROUPS.scanner,
	label: "Card scanner",
	icon: ScanLine,
	pages: [
		{
			title: "Card scanner",
			icon: ScanLine,
			tabs: [
				{
					title: "Training jobs",
					href: "/card-scanner",
					icon: BrainCircuit,
					description: "Scanner model training runs",
					requiredRoles: SCANNER_ROLES,
				},
				{
					title: "Models",
					href: "/card-scanner/models",
					icon: Layers,
					description: "Trained scanner models and which one is live",
					requiredRoles: SCANNER_ROLES,
				},
				{
					title: "Eval suites",
					href: "/card-scanner/eval-suites",
					icon: ListChecks,
					description: "Pinned Roboflow versions every eval job scores against",
					requiredRoles: SCANNER_ROLES,
				},
				{
					title: "Eval jobs",
					href: "/card-scanner/eval-jobs",
					icon: ClipboardCheck,
					description: "Model evaluations and their verdicts",
					requiredRoles: SCANNER_ROLES,
				},
				{
					title: "Signature runs",
					href: "/card-scanner/signature-runs",
					icon: Fingerprint,
					description:
						"Signature database generation, one run per game and embedder version",
					requiredRoles: SCANNER_ROLES,
				},
				{
					title: "Labels",
					href: "/card-scanner/labels",
					icon: Tag,
					description: "Append-only label registry used by trained classifiers",
					requiredRoles: SCANNER_ROLES,
				},
				{
					title: "Manifest",
					href: "/card-scanner/manifest",
					icon: ScrollText,
					description: "Live preview of the v2 scanner manifest",
					requiredRoles: SCANNER_ROLES,
				},
			],
		},
	],
}

const API_ROLES = [AdminRole.IntegrationsAdmin]

const apiGroup: NavGroupConfig = {
	key: NAV_GROUPS.api,
	label: "API",
	icon: Plug,
	pages: [
		{
			title: "Integration apps",
			icon: Plug,
			tabs: [
				{
					title: "Integration apps",
					href: "/integration-apps",
					icon: Plug,
					description: "Mint and manage third-party integration apps",
					requiredRoles: API_ROLES,
				},
			],
		},
		{
			title: "Requests",
			icon: Inbox,
			tabs: [
				{
					title: "Requests",
					href: "/api-requests",
					activePaths: [
						"/integration-apps/listing-requests",
						"/integration-apps/permission-requests",
					],
					icon: Inbox,
					description:
						"API access requests, listing changes and permission or price data requests, waiting for a decision",
					requiredRoles: API_ROLES,
				},
			],
		},
		{
			title: "Price data access",
			icon: Tags,
			tabs: [
				{
					title: "Price data access",
					href: "/price-data-access",
					icon: Tags,
					description:
						"Who may read price data from other marketplaces through the API, and who was refused",
					requiredRoles: API_ROLES,
				},
			],
		},
		{
			title: "Usage",
			icon: Activity,
			tabs: [
				{
					title: "Usage",
					href: "/api-usage",
					icon: Activity,
					description:
						"Who calls the public API, what they call, and what is unused",
					requiredRoles: API_ROLES,
				},
			],
		},
		{
			title: "Adoption gaps",
			icon: Gauge,
			tabs: [
				{
					title: "Adoption gaps",
					href: "/api-usage/adoption-gaps",
					icon: Gauge,
					description:
						"Credentials, grants and keys that exist but have never been used",
					requiredRoles: API_ROLES,
				},
			],
		},
		{
			title: "Access settings",
			icon: KeyRound,
			tabs: [
				{
					title: "Access settings",
					href: "/configuration/api-access",
					icon: KeyRound,
					description:
						"Discord roles and welcome channel used when API access is approved",
					requiredRoles: API_ROLES,
				},
			],
		},
		{
			title: "MCP switches",
			icon: ToggleRight,
			tabs: [
				{
					title: "MCP switches",
					href: "/configuration/mcp",
					icon: ToggleRight,
					description:
						"Turn the MCP cards and signed-out access on or off without a deploy",
					requiredRoles: API_ROLES,
				},
			],
		},
	],
}

function toolsGroup(): NavGroupConfig {
	const pages: NavPage[] = [
		singlePage({
			title: "Site alert",
			href: "/site-alert",
			icon: Siren,
			description: "Site-wide alert banner shown at the top of every page",
		}),
		singlePage({
			title: "Discord bot",
			href: "/discord-bot",
			icon: Bot,
			description: "Bot invocations, configuration and usage",
			requiredRoles: [AdminRole.DiscordRead, AdminRole.DiscordAdmin],
		}),
		singlePage({
			title: "CRM",
			href: "/tools/crm",
			icon: Radio,
			description: "Backfill Braze and Attio profile attributes",
			requiredRoles: [AdminRole.Superadmin],
		}),
	]

	if (!isProduction) {
		pages.push(
			singlePage(
				{
					title: "Dev tools",
					href: "/dev-tools",
					icon: FlaskConical,
					description: "Testing helpers for QA and development",
					requiredRoles: [AdminRole.Superadmin],
				},
				{ badge: "Dev" },
			),
		)
	}

	return { key: NAV_GROUPS.tools, label: "Tools", icon: Wrench, pages }
}

/**
 * Every group of the admin navigation in rail order, unfiltered by roles.
 * Dev tools are left out on the production admin.
 */
export function getNavGroups(): NavGroupConfig[] {
	return [
		{
			key: NAV_GROUPS.overview,
			label: "Overview",
			icon: LayoutDashboard,
			pages: [
				singlePage({
					title: "Dashboard",
					href: "/",
					icon: LayoutDashboard,
					description: "Everything waiting on CardNexus, and today's volume",
				}),
			],
		},
		marketplaceGroup,
		promotionsGroup,
		financeGroup,
		catalogueGroup,
		scannerGroup,
		apiGroup,
		toolsGroup(),
		{
			key: NAV_GROUPS.system,
			label: "System",
			icon: Shield,
			pages: [
				singlePage({
					title: "Admin users",
					href: "/admin-users",
					icon: Shield,
					description: "Manage admin users and their roles",
					requiredRoles: [AdminRole.Superadmin],
				}),
				singlePage({
					title: "Client verification",
					href: "/configuration/client-verification",
					icon: ShieldCheck,
					description:
						"Per-gate modes and budgets of the private API bot protection",
					requiredRoles: [AdminRole.Superadmin],
				}),
				singlePage({
					title: "Internal passes",
					href: "/configuration/internal-passes",
					icon: KeyRound,
					description:
						"Passes that let scripts, tests and agents skip the client checks",
					requiredRoles: [AdminRole.Superadmin],
				}),
			],
		},
	]
}

/** Every tab of every page as one flat list, unfiltered by roles. */
export function getNavigation(): NavItem[] {
	return getNavGroups().flatMap((group) =>
		group.pages.flatMap((page) =>
			page.tabs.map(
				(tab): NavItem => ({
					...tab,
					group: group.key,
					groupLabel: group.label,
					pageTitle: page.title,
					badge: page.badge,
				}),
			),
		),
	)
}

/**
 * The name of a tab outside its page: the page title alone when they match,
 * otherwise "Page: Tab" (for example "Accounting: VAT declarations").
 */
export function navItemTitle(item: NavItem): string {
	return item.pageTitle === item.title
		? item.title
		: `${item.pageTitle}: ${item.title}`
}
